import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
// Reuse the REAL editor's stylesheet so this replica is pixel-faithful.
// dashboard.css is fully scoped under .rs-ed* / .rs-app and builds on the
// rush.css tokens the landing page already loads — importing it here only
// styles the markup we opt into below.
import '../../components/DashboardHome/dashboard.css';
import '../../components/DashboardHome/rush-fixes.css';

/* ── Icons copied from the real editor (src/components/RushPages/Editor) ── */
const Icn = {
  back: (<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M8 3L4 7l4 4M4 7h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  caret: (<svg className="caret" width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  check: (<svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  reject: (<svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>),
  ai: (<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2l1.4 3.1L12.5 6 9.4 7.4 8 10.5 6.6 7.4 3.5 6l3.1-.9L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>),
  memory: (<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5" /><path d="M8 5v3l2 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>),
  glossary: (<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 3h7l3 3v7H3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><path d="M5 7h6M5 9.5h6M5 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>),
  comment: (<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2.5 8a5 5 0 015-5h1a5 5 0 015 5v3a1 1 0 01-1 1H8L5 14v-2H3.5a1 1 0 01-1-1V8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>),
  alert: (<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2L1.5 13h13L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><path d="M8 6v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><circle cx="8" cy="11" r="0.7" fill="currentColor" /></svg>),
  okCirc: (<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" /><path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  filter: (<svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 3h10l-4 5v4l-2-1V8L2 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>),
};

const STATUS_LABEL = { approved: 'Approved', translated: 'Translated', untranslated: 'Untranslated' };
const STATUS_FILTERS = ['All', 'Untranslated', 'Translated', 'Approved'];
const tmCls = (pct) => (pct == null ? 'low' : pct >= 90 ? '' : pct >= 75 ? 'med' : 'low');

const LANGS = [
  { code: 'DE', name: 'German' },
  { code: 'FR', name: 'French' },
  { code: 'ES', name: 'Spanish' },
  { code: 'IT', name: 'Italian' },
  { code: 'PT', name: 'Portuguese' },
  { code: 'JA', name: 'Japanese' },
];

// Canned content. Each segment carries: English source, optional glossary term,
// and per-language { text, conf } plus the locked glossary target per language.
const SEGMENTS = [
  {
    source: 'Welcome to your workspace.',
    gloss: null,
    t: {
      DE: { text: 'Willkommen in Ihrem Workspace.', conf: 98 },
      FR: { text: 'Bienvenue dans votre espace de travail.', conf: 96 },
      ES: { text: 'Bienvenido a tu espacio de trabajo.', conf: 97 },
      IT: { text: 'Benvenuto nel tuo spazio di lavoro.', conf: 95 },
      PT: { text: 'Bem-vindo ao seu espaço de trabalho.', conf: 94 },
      JA: { text: 'ワークスペースへようこそ。', conf: 92 },
    },
    tm: [
      { conf: 98, source: 'Welcome to your translation workspace.', t: { DE: 'Willkommen in Ihrem Übersetzungs-Workspace.', FR: 'Bienvenue dans votre espace de traduction.', ES: 'Bienvenido a tu espacio de traducción.', IT: 'Benvenuto nel tuo spazio di traduzione.', PT: 'Bem-vindo ao seu espaço de tradução.', JA: '翻訳ワークスペースへようこそ。' } },
      { conf: 82, source: 'Welcome to the team.', t: { DE: 'Willkommen im Team.', FR: "Bienvenue dans l'équipe.", ES: 'Bienvenido al equipo.', IT: 'Benvenuto nel team.', PT: 'Bem-vindo à equipa.', JA: 'チームへようこそ。' } },
    ],
  },
  {
    source: 'Rush Studio brings your entire localization pipeline into one calm dashboard.',
    gloss: 'localization',
    glossContext: "Use the en-US spelling 'localization' across all product copy.",
    glossTarget: { DE: 'Lokalisierung', FR: 'localisation', ES: 'localización', IT: 'localizzazione', PT: 'localização', JA: 'ローカライズ' },
    t: {
      DE: { text: 'Rush Studio vereint Ihre gesamte Lokalisierungs-Pipeline in einem ruhigen Dashboard.', conf: 88 },
      FR: { text: 'Rush Studio réunit tout votre pipeline de localisation dans un tableau de bord épuré.', conf: 86 },
      ES: { text: 'Rush Studio reúne todo tu flujo de localización en un único panel tranquilo.', conf: 87 },
      IT: { text: "Rush Studio riunisce l'intera pipeline di localizzazione in un'unica dashboard ordinata.", conf: 84 },
      PT: { text: 'O Rush Studio reúne todo o seu fluxo de localização num painel tranquilo.', conf: 85 },
      JA: { text: 'Rush Studio は、ローカライズの全工程を一つの落ち着いたダッシュボードにまとめます。', conf: 81 },
    },
    tm: [
      { conf: 76, source: 'Bring your whole pipeline into one dashboard.', t: { DE: 'Bringen Sie Ihre gesamte Pipeline in ein Dashboard.', FR: 'Réunissez tout votre pipeline dans un seul tableau de bord.', ES: 'Reúne todo tu flujo en un solo panel.', IT: "Riunisci l'intera pipeline in un'unica dashboard.", PT: 'Reúna todo o seu fluxo num único painel.', JA: 'すべての工程を一つのダッシュボードに。' } },
    ],
  },
  {
    source: 'Your free trial starts today — no credit card required.',
    gloss: null,
    t: {
      DE: { text: 'Ihre kostenlose Testphase beginnt heute – keine Kreditkarte erforderlich.', conf: 91 },
      FR: { text: "Votre essai gratuit commence aujourd'hui — sans carte bancaire.", conf: 90 },
      ES: { text: 'Tu prueba gratuita empieza hoy: sin tarjeta de crédito.', conf: 93 },
      IT: { text: 'La tua prova gratuita inizia oggi, senza carta di credito.', conf: 89 },
      PT: { text: 'O seu teste gratuito começa hoje — sem cartão de crédito.', conf: 88 },
      JA: { text: '無料トライアルは今日から開始。クレジットカードは不要です。', conf: 84 },
    },
    tm: [],
  },
  {
    source: 'Ship in 100+ languages without the tab graveyard.',
    gloss: null,
    t: {
      DE: { text: 'Veröffentlichen Sie in über 100 Sprachen – ohne Tab-Chaos.', conf: 82 },
      FR: { text: "Publiez dans plus de 100 langues, sans la jungle d'onglets.", conf: 80 },
      ES: { text: 'Publica en más de 100 idiomas, sin el caos de pestañas.', conf: 83 },
      IT: { text: 'Pubblica in oltre 100 lingue, senza il caos di schede.', conf: 79 },
      PT: { text: 'Publique em mais de 100 idiomas, sem o caos de abas.', conf: 81 },
      JA: { text: '100以上の言語で公開。タブの山とはお別れです。', conf: 76 },
    },
    tm: [],
  },
  {
    source: 'Every approved sentence is saved to translation memory.',
    gloss: 'translation memory',
    glossContext: 'Reusable store of approved source–target pairs.',
    glossTarget: { DE: 'Übersetzungsspeicher', FR: 'mémoire de traduction', ES: 'memoria de traducción', IT: 'memoria di traduzione', PT: 'memória de tradução', JA: '翻訳メモリ' },
    t: {
      DE: { text: 'Jeder freigegebene Satz wird im Übersetzungsspeicher gespeichert.', conf: 86 },
      FR: { text: 'Chaque phrase approuvée est enregistrée dans la mémoire de traduction.', conf: 85 },
      ES: { text: 'Cada frase aprobada se guarda en la memoria de traducción.', conf: 88 },
      IT: { text: 'Ogni frase approvata viene salvata nella memoria di traduzione.', conf: 85 },
      PT: { text: 'Cada frase aprovada é guardada na memória de tradução.', conf: 84 },
      JA: { text: '承認された文章はすべて翻訳メモリに保存されます。', conf: 82 },
    },
    tm: [
      { conf: 90, source: 'Approved sentences are saved to memory.', t: { DE: 'Freigegebene Sätze werden im Speicher gespeichert.', FR: 'Les phrases approuvées sont enregistrées dans la mémoire.', ES: 'Las frases aprobadas se guardan en la memoria.', IT: 'Le frasi approvate vengono salvate nella memoria.', PT: 'As frases aprovadas são guardadas na memória.', JA: '承認された文章はメモリに保存されます。' } },
    ],
  },
];

// Highlight glossary source terms inside a string, like the real editor.
const withGloss = (text, terms = []) => {
  if (!terms.length || !text) return text;
  const escaped = terms.map((g) => g.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
  return text.split(pattern).map((p, i) => {
    const hit = terms.some((g) => g.toLowerCase() === p.toLowerCase());
    return hit ? <span key={i} className="gloss" title="Glossary term">{p}</span> : <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

// Seed: segment 0 approved, segment 1 translated, rest untranslated — gives
// the editor a believable "work in progress" state on first paint.
const seedFor = (lang) => ({
  0: { text: SEGMENTS[0].t[lang].text, conf: SEGMENTS[0].t[lang].conf, approved: true },
  1: { text: SEGMENTS[1].t[lang].text, conf: SEGMENTS[1].t[lang].conf, approved: false },
});

const RushTryDemo = () => {
  const [lang, setLang] = useState('DE');
  const [langOpen, setLangOpen] = useState(false);
  const [active, setActive] = useState(2);
  const [navFilter, setNavFilter] = useState('All');
  const [cxTab, setCxTab] = useState('matches');
  const [cells, setCells] = useState(() => seedFor('DE'));
  const [ai, setAi] = useState({ seg: null, status: 'idle', text: '' }); // status: idle|thinking|typing|ready
  const timers = useRef([]);
  const langRef = useRef(null);

  const langName = (LANGS.find((l) => l.code === lang) || {}).name;
  const total = SEGMENTS.length;

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => clearTimers(), []);

  // Reset everything when the target language changes.
  const switchLang = (code) => {
    clearTimers();
    setLang(code);
    setLangOpen(false);
    setCells(seedFor(code));
    setAi({ seg: null, status: 'idle', text: '' });
  };

  useEffect(() => {
    if (!langOpen) return undefined;
    const onDoc = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [langOpen]);

  const statusOf = (i) => {
    const c = cells[i];
    if (!c || !c.text) return 'untranslated';
    return c.approved ? 'approved' : 'translated';
  };

  const statusCounts = useMemo(() => {
    const counts = { All: total, Untranslated: 0, Translated: 0, Reviewed: 0, Approved: 0, Flagged: 0 };
    SEGMENTS.forEach((_, i) => {
      const s = statusOf(i);
      if (s === 'untranslated') counts.Untranslated += 1;
      else if (s === 'approved') counts.Approved += 1;
      else counts.Translated += 1;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells]);

  const translatedCount = SEGMENTS.filter((_, i) => cells[i] && cells[i].text).length;
  const approvedCount = SEGMENTS.filter((_, i) => cells[i] && cells[i].approved).length;
  const translatedPct = Math.round((translatedCount / total) * 100);
  const approvedPct = Math.round((approvedCount / total) * 100);

  const setTarget = (i, text, conf, approved = false) =>
    setCells((prev) => ({ ...prev, [i]: { text, conf: conf != null ? conf : (prev[i] && prev[i].conf) || null, approved } }));

  const generateAI = (i) => {
    clearTimers();
    const full = SEGMENTS[i].t[lang].text;
    setAi({ seg: i, status: 'thinking', text: '' });
    timers.current.push(setTimeout(() => {
      const chars = Array.from(full);
      setAi({ seg: i, status: 'typing', text: '' });
      chars.forEach((_, k) => {
        timers.current.push(setTimeout(() => {
          setAi({ seg: i, status: k === chars.length - 1 ? 'ready' : 'typing', text: chars.slice(0, k + 1).join('') });
        }, k * 20));
      });
    }, 520));
  };

  const replaceWithAI = (i) => {
    setTarget(i, SEGMENTS[i].t[lang].text, SEGMENTS[i].t[lang].conf, false);
    setAi({ seg: null, status: 'idle', text: '' });
  };

  const confirmNext = (i) => {
    const text = (cells[i] && cells[i].text) || SEGMENTS[i].t[lang].text;
    setTarget(i, text, (cells[i] && cells[i].conf) || SEGMENTS[i].t[lang].conf, true);
    const next = SEGMENTS.findIndex((_, k) => k > i && !(cells[k] && cells[k].approved));
    if (next !== -1) { setActive(next); setAi({ seg: null, status: 'idle', text: '' }); }
  };

  const applyTM = (i, m) => { setTarget(i, m.t[lang], m.conf, false); };
  const reject = (i) => { setTarget(i, '', null, false); setAi({ seg: null, status: 'idle', text: '' }); };

  const seg = SEGMENTS[active];
  const cell = cells[active] || { text: '', conf: null, approved: false };
  const glossTermsForActive = seg.gloss ? [seg.gloss] : [];
  const tmMatches = seg.tm || [];

  // Canned QA issues for the active segment.
  const issues = useMemo(() => {
    const list = [];
    if (!cell.text) list.push({ kind: 'warn', title: 'Not yet translated', desc: 'This segment has no target text.' });
    else list.push({ kind: 'ok', title: 'Length within bounds', desc: 'Target fits the source layout.' });
    if (seg.gloss) {
      const applied = cell.text && cell.text.includes(seg.glossTarget[lang]);
      list.push(applied
        ? { kind: 'ok', title: 'Glossary term applied', desc: `"${seg.gloss}" → "${seg.glossTarget[lang]}" (locked).` }
        : { kind: 'warn', title: 'Glossary term missing', desc: `Expected the locked term for "${seg.gloss}".` });
    }
    list.push({ kind: 'ok', title: 'No tag mismatches', desc: 'Placeholders and formatting preserved.' });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, cell.text, lang]);

  const navItems = SEGMENTS.map((s, i) => ({ src: s.source, i, status: statusOf(i) }));
  const navFiltered = navFilter === 'All' ? navItems : navItems.filter((it) => STATUS_LABEL[it.status] === navFilter);

  const setActiveSeg = (i) => { setActive(i); setCxTab('matches'); setAi({ seg: null, status: 'idle', text: '' }); };

  return (
    <section className="section try-demo">
      <div className="rs-container">
        <div className="section-head reveal">
          <div className="eyebrow">Try it live</div>
          <h2>This is the actual editor.<br />Go ahead — translate a segment.</h2>
          <p className="section-lead">
            A working slice of Rush Studio's translation workspace. Pick a language, click any segment to
            focus it, generate an AI draft, apply a memory match, and confirm — exactly what your team does all day.
          </p>
        </div>

        <div className="try-shell reveal reveal-scale">
          <div className="rs-ed">
            {/* ── Top bar ── */}
            <div className="rs-ed-bar">
              <button type="button" className="back">{Icn.back}<span>Back</span></button>
              <div className="file-ic">DOC</div>
              <div className="title-block">
                <div className="pn">Q4 Product Update.docx</div>
                <div className="crm">{total} segments · demo</div>
              </div>
              <div className="rs-ed-lang-switch" ref={langRef} style={{ position: 'relative' }}>
                <button type="button" onClick={() => setLangOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 0, padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>
                  <span className="pair">EN <span className="arr">→</span> {lang}</span>
                  {Icn.caret}
                </button>
                {langOpen && (
                  <div className="rs-ed-lang-menu" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, background: 'var(--bg-card)', border: '1px solid var(--line)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', minWidth: 200, zIndex: 50, padding: 4 }}>
                    {LANGS.map((l) => {
                      const isCurrent = l.code === lang;
                      return (
                        <button key={l.code} type="button" onClick={() => switchLang(l.code)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', padding: '8px 10px', background: isCurrent ? 'var(--bg-soft)' : 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', font: 'inherit', color: 'inherit', textAlign: 'left' }}>
                          <span>EN <span className="arr">→</span> {l.code} · {l.name}</span>
                          {isCurrent && <span style={{ opacity: 0.6, fontSize: 12 }}>Current</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="rs-ed-progress">
                <div className="pmeta">
                  <span>Segment <b>{active + 1}</b> / <b>{total}</b></span>
                  <span><b>{translatedPct}%</b> translated{approvedPct > 0 && <span style={{ marginLeft: 8, color: 'var(--green)' }}>✓ {approvedPct}% approved</span>}</span>
                </div>
                <div className="pbar" style={{ position: 'relative' }}>
                  <span style={{ width: `${translatedPct}%` }} />
                  {approvedPct > 0 && <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${approvedPct}%`, background: 'var(--green)', borderRadius: 'inherit', pointerEvents: 'none' }} />}
                </div>
              </div>
              <span className="spacer" />
              <span className="save-status"><span className="dt" />All changes saved</span>
            </div>

            {/* ── Body: navigator · segments · context ── */}
            <div className="rs-ed-body">
              {/* Left: navigator */}
              <aside className="rs-ed-snav">
                <div className="rs-ed-snav-h">
                  <h3>Segments — {navFiltered.length}</h3>
                  <div className="rs-ed-snav-filters">
                    {STATUS_FILTERS.map((f) => (
                      <button key={f} type="button" className={`rs-ed-snav-filter ${navFilter === f ? 'on' : ''}`} onClick={() => setNavFilter(f)}>
                        {f} <span style={{ opacity: 0.7 }}>{statusCounts[f] || 0}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="rs-ed-snav-list">
                  {navFiltered.length === 0 && <div style={{ padding: 24, color: 'var(--ink-4)', fontSize: 12.5, textAlign: 'center' }}>Nothing matches this filter.</div>}
                  {navFiltered.map(({ src, i, status }) => (
                    <button type="button" key={i} className={`rs-ed-snav-item ${active === i ? 'active' : ''}`} onClick={() => setActiveSeg(i)}>
                      <span className="sn">{i + 1}</span>
                      <span className="stxt">{src.slice(0, 80)}</span>
                      <span className={`sdot ${status}`} />
                    </button>
                  ))}
                </div>
              </aside>

              {/* Center: segment list */}
              <main className="rs-ed-main">
                <div className="rs-ed-main-h">
                  <div className="h-info">
                    <b>Editing in-context</b>
                    <span>Showing 1–{total} of {total} · click any to focus</span>
                  </div>
                  <span className="spacer" />
                  <button type="button" className="btn-q">{Icn.filter}<span>Filter view</span></button>
                </div>
                <div className="rs-ed-segs">
                  {SEGMENTS.map((s, i) => {
                    const status = statusOf(i);
                    if (i !== active) {
                      const c = cells[i];
                      return (
                        <button type="button" key={i} className="rs-ed-seg" onClick={() => setActiveSeg(i)}>
                          <div className="sn-col"><span className="sn-n">{i + 1}</span><span className={`sn-dot ${status}`} /></div>
                          <div className="src-pane">{s.source}</div>
                          <div className={`tgt-pane ${status === 'untranslated' ? 'empty' : ''}`}>{status === 'untranslated' ? '—' : c.text}</div>
                          <div className="meta-col">
                            <span className={`tm-match ${tmCls(c && c.conf)}`}>{c && c.conf != null ? `${Math.round(c.conf)}%` : '—'}</span>
                            <span className={`seg-status ${status}`}>{STATUS_LABEL[status]}</span>
                          </div>
                        </button>
                      );
                    }
                    // Active (expanded) segment
                    const aiHere = ai.seg === i ? ai : { status: 'idle', text: '' };
                    return (
                      <div className="rs-ed-seg active" key={i}>
                        <div className="a-top">
                          <span className="a-num">Segment <b>{i + 1}</b> / {total}</span>
                          <span className={`seg-status ${status}`}>{STATUS_LABEL[status]}</span>
                          <span className="spacer" />
                          <span className="a-meta">
                            <b>{cell.conf != null ? `${cell.conf}%` : '—'}</b> match
                            <span style={{ width: 3, height: 3, borderRadius: 999, background: 'var(--ink-4)' }} />
                            <b>EN → {lang}</b>
                          </span>
                        </div>

                        <div className="a-panes">
                          <div className="a-pane src">
                            <div className="a-lbl">Source · EN</div>
                            <div className="a-text">{withGloss(s.source, glossTermsForActive)}</div>
                          </div>
                          <div className="a-pane tgt">
                            <div className="a-lbl">Target · {langName}</div>
                            <textarea
                              className="a-input"
                              value={cell.text || ''}
                              placeholder="Translate this segment…"
                              onChange={(e) => setTarget(i, e.target.value, cell.conf, false)}
                              rows={2}
                            />
                          </div>
                        </div>

                        <div className="a-ai">
                          <div className="ai-ic">{Icn.ai}</div>
                          <div className="ai-body">
                            <div className="ai-lbl">
                              AI suggestion · Rush AI
                              {aiHere.status === 'thinking' && <span style={{ marginLeft: 8, color: 'var(--ink-4)' }}>thinking…</span>}
                              {aiHere.status === 'typing' && <span style={{ marginLeft: 8, color: 'var(--ink-4)' }}>generating…</span>}
                            </div>
                            {aiHere.status === 'thinking' && <div className="ai-txt" style={{ color: 'var(--ink-4)' }}>Analysing source &amp; memory…</div>}
                            {(aiHere.status === 'typing' || aiHere.status === 'ready') && (
                              <>
                                <div className="ai-txt">{withGloss(aiHere.text, glossTermsForActive)}{aiHere.status === 'typing' && <span className="try-caret" />}</div>
                                {aiHere.status === 'ready' && (
                                  <div className="ai-actions">
                                    <button type="button" className="ai-btn primary" onClick={() => replaceWithAI(i)}>{Icn.check} Replace</button>
                                    <button type="button" className="ai-btn" onClick={() => generateAI(i)}>Regenerate</button>
                                    <button type="button" className="ai-btn" onClick={() => setAi({ seg: null, status: 'idle', text: '' })}>Dismiss</button>
                                  </div>
                                )}
                              </>
                            )}
                            {aiHere.status === 'idle' && (
                              <div className="ai-actions">
                                <button type="button" className="ai-btn primary" onClick={() => generateAI(i)}>{Icn.ai} Generate translation</button>
                                <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>or press Ctrl/⌘ + /</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="a-foot">
                          <span className="hint"><span className="kbd">⌘</span> + <span className="kbd">⏎</span> confirm &amp; next</span>
                          <span className="spacer" />
                          <button type="button" className="a-btn warn" onClick={() => reject(i)}>{Icn.reject} Reject</button>
                          <button type="button" className="a-btn primary" onClick={() => confirmNext(i)} disabled={!(cell.text || '').trim()}>{Icn.check} Confirm &amp; next</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </main>

              {/* Right: context panel */}
              <aside className="rs-ed-cx">
                <div className="rs-ed-cx-tabs">
                  {[
                    ['matches', 'Matches', Icn.memory, tmMatches.length],
                    ['glossary', 'Glossary', Icn.glossary, glossTermsForActive.length],
                    ['comments', 'Comments', Icn.comment, 0],
                    ['issues', 'Issues', Icn.alert, issues.filter((x) => x.kind !== 'ok').length],
                  ].map(([key, name, ic, ct]) => (
                    <button key={key} type="button" className={`rs-ed-cx-tab ${cxTab === key ? 'on' : ''}`} onClick={() => setCxTab(key)}>
                      {ic}<span>{name}</span><span className="ct">{ct}</span>
                    </button>
                  ))}
                </div>
                <div className="rs-ed-cx-body">
                  {cxTab === 'matches' && (
                    <>
                      <div className="rs-ed-cx-sec">Translation memory · {tmMatches.length} matches</div>
                      {tmMatches.length === 0 && <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12.5 }}>No matches yet for this segment.</div>}
                      {tmMatches.map((m, i) => (
                        <div key={i} className="rs-ed-tm">
                          <div className="tm-head">
                            <span className={`tm-m ${tmCls(m.conf)}`}>{m.conf}%</span>
                            <span className="tm-origin"><b>Memory</b></span>
                            <button type="button" className="tm-apply" onClick={() => applyTM(active, m)}>Apply</button>
                          </div>
                          <div className="tm-src">{m.source}</div>
                          <div className="tm-tgt">{m.t[lang]}</div>
                        </div>
                      ))}
                    </>
                  )}
                  {cxTab === 'glossary' && (
                    <>
                      <div className="rs-ed-cx-sec">Terms found in segment {active + 1}</div>
                      {glossTermsForActive.length === 0 && <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12.5 }}>No glossary terms match this segment.</div>}
                      {seg.gloss && (
                        <div className="rs-ed-gl-item">
                          <div className="term-row">
                            <span className="term-src">{seg.gloss}</span>
                            <span className="arr">→</span>
                            <span className="term-tgt">{seg.glossTarget[lang]}</span>
                            <span className="term-lock">LOCKED</span>
                          </div>
                          {seg.glossContext && <div className="term-def">{seg.glossContext}</div>}
                        </div>
                      )}
                    </>
                  )}
                  {cxTab === 'comments' && (
                    <>
                      <div className="rs-ed-cx-sec">Comments on segment {active + 1}</div>
                      <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12.5 }}>No comments yet. In the full editor your team can discuss any segment here.</div>
                    </>
                  )}
                  {cxTab === 'issues' && (
                    <>
                      <div className="rs-ed-cx-sec">QA checks on segment {active + 1}</div>
                      {issues.map((iss, i) => (
                        <div key={i} className={`rs-ed-iss-item ${iss.kind}`}>
                          <span className="iss-ic">{iss.kind === 'ok' ? Icn.okCirc : Icn.alert}</span>
                          <div className="iss-body"><div className="iss-t">{iss.title}</div><div className="iss-d">{iss.desc}</div></div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </aside>
            </div>

            {/* ── Foot bar ── */}
            <div className="rs-ed-foot">
              <span className="item"><span className="kbd">⌘</span><span className="kbd">⏎</span><span>Confirm &amp; next</span></span>
              <span className="item"><span className="kbd">⌘</span><span className="kbd">/</span><span>AI suggest</span></span>
              <span className="item"><span className="kbd">⌘</span><span className="kbd">M</span><span>Apply top TM</span></span>
              <span className="spacer" />
              <span className="bdg-ai"><span className="dot" />Rush AI · ready</span>
            </div>
          </div>
        </div>

        <div className="try-foot-cta reveal">
          <span>This is a canned preview with a handful of segments.</span>
          <Link to="/register" className="btn btn-accent">Open the real editor — free</Link>
        </div>
      </div>
    </section>
  );
};

export default RushTryDemo;
